/**
 * ImportDropzone - File upload component for CSV import
 * 
 * Features:
 * - Drag & drop support
 * - File validation (type, size)
 * - Template download
 * - Visual feedback states
 */

import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  Upload,
  FileText,
  Download,
  X,
  FileWarning,
  Loader2,
} from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * ImportDropzone component
 * 
 * @param {Object} props
 * @param {Function} props.onFileSelect - Called when file is selected
 * @param {Function} props.onUpload - Called when user clicks upload button
 * @param {Function} props.onDownloadTemplate - Download CSV template
 * @param {File|null} props.file - Currently selected file
 * @param {boolean} props.uploading - Whether upload is in progress
 * @param {string|null} props.error - Error code to display
 * @param {string} props.entityType - Entity type being imported
 */
export function ImportDropzone({
  onFileSelect,
  onUpload,
  onDownloadTemplate,
  file,
  uploading = false,
  error = null,
  entityType,
}) {
  const { t, language } = useLanguage();
  const [dragActive, setDragActive] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      onFileSelect(acceptedFiles[0]);
    }
  }, [onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
    },
    maxFiles: 1,
    disabled: uploading,
    onDragEnter: () => setDragActive(true),
    onDragLeave: () => setDragActive(false),
  });

  const handleClearFile = (e) => {
    e.stopPropagation();
    onFileSelect(null);
  };

  const handleUpload = (e) => {
    e.stopPropagation();
    onUpload();
  };

  const handleDownloadTemplate = (e) => {
    e.stopPropagation();
    onDownloadTemplate(language === 'cs' ? 'cs' : 'en');
  };

  // Get error message from translation key
  const getErrorMessage = (errorCode) => {
    if (!errorCode) return null;
    return t.import?.errors?.[errorCode] || t.import?.[errorCode] || errorCode;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={cn(
          'relative border-2 border-dashed rounded-lg p-8 transition-all duration-200 cursor-pointer',
          'hover:border-primary/50 hover:bg-primary/5',
          isDragActive || dragActive
            ? 'border-primary bg-primary/10'
            : 'border-slate-200 bg-slate-50/50',
          uploading && 'pointer-events-none opacity-60',
          error && !file && 'border-red-300 bg-red-50/30'
        )}
        data-testid="import-dropzone"
      >
        <input {...getInputProps()} data-testid="import-file-input" />
        
        <div className="flex flex-col items-center justify-center text-center space-y-4">
          {uploading ? (
            <>
              <Loader2 className="h-12 w-12 text-primary animate-spin" />
              <div>
                <p className="text-lg font-medium text-slate-700">
                  {t.common?.uploading || 'Uploading...'}
                </p>
              </div>
            </>
          ) : file ? (
            <>
              <div className="flex items-center gap-3 p-4 bg-white rounded-lg border shadow-sm">
                <FileText className="h-10 w-10 text-emerald-500" />
                <div className="text-left">
                  <p className="font-medium text-slate-800 truncate max-w-xs">
                    {file.name}
                  </p>
                  <p className="text-sm text-slate-500">
                    {t.import?.fileSize || 'Size'}: {formatFileSize(file.size)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFile}
                  className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                  data-testid="clear-file-btn"
                  aria-label={t.import?.changeFile || 'Remove file'}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="gap-2"
                  data-testid="upload-validate-btn"
                >
                  <Upload className="h-4 w-4" />
                  {t.import?.uploadAndValidate || 'Upload & Validate'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleClearFile}
                  disabled={uploading}
                  data-testid="change-file-btn"
                >
                  {t.import?.changeFile || 'Change File'}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className={cn(
                'p-4 rounded-full',
                error ? 'bg-red-100' : 'bg-primary/10'
              )}>
                {error ? (
                  <FileWarning className="h-12 w-12 text-red-500" />
                ) : (
                  <Upload className="h-12 w-12 text-primary" />
                )}
              </div>
              
              <div>
                <p className="text-lg font-medium text-slate-700">
                  {t.import?.dropzoneTitle || 'Drop CSV file here'}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {t.import?.dropzoneSubtitle || 'or click to select file'}
                </p>
              </div>
              
              <p className="text-xs text-slate-400 max-w-sm">
                {t.import?.dropzoneHint || 'Supported: UTF-8, CP1250 | Separators: comma, semicolon'}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="import-error">
          <FileWarning className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{getErrorMessage(error)}</p>
        </div>
      )}

      {/* Template download */}
      <Card className="bg-slate-50/50 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg border">
                <FileText className="h-5 w-5 text-slate-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">
                  {t.import?.downloadTemplate || 'Download Template'}
                </p>
                <p className="text-xs text-slate-500">
                  {entityType && t.import?.entityTypes?.[entityType]}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              className="gap-2"
              data-testid="download-template-btn"
            >
              <Download className="h-4 w-4" />
              {language === 'cs' ? 'CS' : 'EN'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ImportDropzone;
